import { useGetDogBreedInterestSubscription } from '@application/api';
import { SkeletonBox, SubscribeButton } from '@presentation/components';
import { BREAKPOINTS } from '@presentation/styles';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { TbBellRinging } from 'react-icons/tb';
import { useMediaQuery } from 'react-responsive';
import { Tooltip } from 'react-tooltip';
import { useToggleDogBreedInDogBreedInterestSubscription } from './api/toggleDogBreedInDogBreedInterestSubscription';
import type { SubscribeToDogBreedButtonControllerProps } from './types';

export * from './types';

export const SubscribeToDogBreedButtonController: FC<SubscribeToDogBreedButtonControllerProps> = (
  props,
) => {
  const {
    breedName,
    hasADog,
    iconOnlyMobile,
    subscriptionEmail,
    openTooltipOnClick,
    onFallbackClick,
  } = props;

  const { translate } = useTranslation();
  const {
    toggleDogBreedInDogBreedInterestSubscriptionFromButton,
    toggleDogBreedInDogBreedInterestSubscriptionLoading,
  } = useToggleDogBreedInDogBreedInterestSubscription();

  const { dogBreedInterestSubscription, dogBreedInterestSubscriptionLoading } =
    useGetDogBreedInterestSubscription(subscriptionEmail);

  const isSubscribed = !!dogBreedInterestSubscription?.dogBreeds.find(
    (dogBreed) => dogBreed.name === breedName,
  );

  const isMobile = useMediaQuery({ query: `(max-width: ${BREAKPOINTS.medium}px)` });

  if (dogBreedInterestSubscriptionLoading) {
    return <SkeletonBox style={{ width: '40px', height: '32px' }} />;
  }

  return (
    <>
      <Tooltip id="breed-subscribe-tooltip" openOnClick={openTooltipOnClick ?? isMobile} />

      <SubscribeButton
        iconOnlyMobile={iconOnlyMobile}
        active={isSubscribed}
        data-tooltip-id="breed-subscribe-tooltip"
        data-tooltip-html={
          isSubscribed
            ? translate('common.sections.breedInterestSubscription.alreadySubscribedToBreedTooltip')
            : translate('common.sections.breedInterestSubscription.subscribeToBreedTooltip')
        }
        color={isSubscribed ? 'blue' : 'lightBlue'}
        shrink
        size="small"
        iconRight={<TbBellRinging />}
        loading={toggleDogBreedInDogBreedInterestSubscriptionLoading}
        onClick={async () => {
          if (breedName && subscriptionEmail) {
            await toggleDogBreedInDogBreedInterestSubscriptionFromButton({
              dogBreedName: breedName,
              email: subscriptionEmail,
              hasADog: !!hasADog,
            });
            return;
          }

          if (onFallbackClick) {
            onFallbackClick();
          }
        }}
      >
        <span>
          {isSubscribed
            ? translate('common.buttons.iamSubscribed')
            : translate('common.buttons.getAlerted')}
        </span>
      </SubscribeButton>
    </>
  );
};
